import React, { useState, useRef, useEffect } from "react";

const AddVocabularyForm = React.memo(({
  showAddForm,
  setShowAddForm,
  isEditMode,
  setIsEditMode,
  editingWord,
  setEditingWord,
  onAddVocabulary,
  playVocabAudio,
  playSentenceAudio,
  stopAllAudio,
  getAudioDuration,
  extractYouTubeVideoId,
  isYouTubeUrl
}) => {
  const [wordJp, setWordJp] = useState("");
  const [wordVi, setWordVi] = useState("");
  const [audioVocab, setAudioVocab] = useState("");
  const [audioSentence, setAudioSentence] = useState("");
  const [sentenceStartTime, setSentenceStartTime] = useState(0);
  const [sentenceEndTime, setSentenceEndTime] = useState(0);
  const [exampleSentence, setExampleSentence] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  // Update form when editing
  useEffect(() => {
    if (isEditMode && editingWord) {
      setWordJp(editingWord.wordJp);
      setWordVi(editingWord.wordVi);
      setAudioVocab(editingWord.audioVocab || "");
      setAudioSentence(editingWord.audioSentence || "");
      setSentenceStartTime(editingWord.sentenceStartTime || 0);
      setSentenceEndTime(editingWord.sentenceEndTime || 0);
      setExampleSentence(editingWord.exampleSentence || "");
      setImageUrl(editingWord.imageUrl || "");
    } else {
      resetForm();
    }
  }, [isEditMode, editingWord]);

  const resetForm = () => {
    setWordJp("");
    setWordVi("");
    setAudioVocab("");
    setAudioSentence("");
    setSentenceStartTime(0);
    setSentenceEndTime(0);
    setExampleSentence("");
    setImageUrl("");
  };

  const handleSubmit = () => {
    if (!wordJp.trim() || !wordVi.trim()) {
      alert("Vui lòng nhập từ tiếng Nhật và tiếng Việt");
      return;
    }

    const wordData = {
      wordJp: wordJp.trim(),
      wordVi: wordVi.trim(),
      audioVocab,
      audioSentence,
      sentenceStartTime,
      sentenceEndTime,
      exampleSentence: exampleSentence.trim(),
      imageUrl: imageUrl.trim(),
    };

    onAddVocabulary(wordData, isEditMode, editingWord);
    resetForm();
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingWord(null);
  };

  const handleClose = () => {
    stopAllAudio();
    setShowAddForm(false);
    setIsEditMode(false);
    setEditingWord(null);
    resetForm();
  };

  const handleAudioSentenceChange = (url) => {
    setAudioSentence(url);
    if (url.trim()) {
      setTimeout(() => getAudioDuration(url), 100);
    } else {
      setSentenceStartTime(0);
      setSentenceEndTime(0);
    }
  };

  if (!showAddForm) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-start justify-center pt-20 z-50 p-4 animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-black/90 border border-cyan-400/50 rounded-2xl shadow-neon-cyan p-4 sm:p-6 max-w-4xl w-full max-h-[92vh] sm:max-h-[88vh] overflow-y-auto backdrop-blur-sm animate-slideUp relative">
        {/* Main Input Section */}
        <div className="space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-6 mb-6">
          {/* Japanese Word */}
          <div className="group">
            <label className="flex items-center gap-2 text-sm font-mono font-bold text-cyan-300 mb-3">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-black/60 border border-cyan-400/50 rounded-lg flex items-center justify-center text-cyan-400 text-xs shadow-neon-cyan">
                日
              </span>
              TỪ TIẾNG NHẬT
              <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={wordJp}
                onChange={(e) => setWordJp(e.target.value)}
                className="w-full px-4 py-3 pl-12 bg-black/60 border border-cyan-400/50 rounded-xl text-cyan-100 placeholder-cyan-500/70 focus:border-cyan-400 focus:shadow-neon-cyan transition-all duration-300 font-mono backdrop-blur-sm"
                placeholder="Nhập từ tiếng Nhật"
              />
            </div>
          </div>

          {/* Vietnamese Meaning */}
          <div className="group">
            <label className="flex items-center gap-2 text-sm font-mono font-bold text-green-300 mb-3">
              <span className="w-6 h-6 sm:w-8 sm:h-8 bg-black/60 border border-green-400/50 rounded-lg flex items-center justify-center text-green-400 text-xs shadow-neon-green">
                VI
              </span>
              NGHĨA TIẾNG VIỆT
              <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={wordVi}
                onChange={(e) => setWordVi(e.target.value)}
                className="w-full px-4 py-3 pl-12 bg-black/60 border border-green-400/50 rounded-xl text-green-100 placeholder-green-500/70 focus:border-green-400 focus:shadow-neon-green transition-all duration-300 font-mono backdrop-blur-sm"
                placeholder="Nhập nghĩa tiếng Việt"
              />
            </div>
          </div>
        </div>

        {/* Image Section */}
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-4 sm:p-4 mb-6 border border-orange-400/50 shadow-neon-orange relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-400 to-transparent animate-slide-right delay-300"></div>
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h3 className="text-base sm:text-xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-500 to-red-400 animate-gradient-x">
                HÌNH ẢNH MINH HỌA
              </h3>
            </div>
            <span className="ml-auto text-xs bg-orange-400/20 border border-orange-400/50 text-orange-300 px-3 py-2 rounded-xl font-mono shadow-neon-orange">
              TÙY CHỌN
            </span>
          </div>

          <div className="relative mb-4">
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-4 py-3 pl-12 bg-black/60 border border-orange-400/50 rounded-xl text-orange-100 placeholder-orange-500/70 focus:border-orange-400 focus:shadow-neon-orange transition-all duration-300 font-mono backdrop-blur-sm"
              placeholder="Dán URL hình ảnh từ Google hoặc nguồn khác"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M10.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 9.5 9.5 7 6.5 10.5 5 9l-2 2.5h9Z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {imageUrl && (
            <div className="mb-4 p-4 bg-black/60 border border-orange-400/50 rounded-xl shadow-neon-orange backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-mono font-bold text-orange-300">XEM TRƯỚC HÌNH ẢNH:</span>
              </div>
              <div className="flex justify-center">
                <img
                  src={imageUrl}
                  alt="Xem trước"
                  className="max-w-full max-h-48 rounded-lg border border-orange-400/30 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                    e.target.nextSibling.style.display = 'none';
                  }}
                />
                <div 
                  className="text-red-400 text-sm font-mono p-4 border border-red-400/50 rounded-lg bg-black/60"
                  style={{ display: 'none' }}
                >
                  ❌ Không thể tải hình ảnh. Vui lòng kiểm tra URL.
                </div>
              </div>
            </div>
          )}

          <div className="text-xs text-orange-300/70 font-mono bg-black/40 border border-orange-400/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-orange-400">💡</span>
              <div>
                <strong>Mẹo:</strong> Chuột phải vào bất kỳ hình ảnh nào từ Google Images → Sao chép địa chỉ hình ảnh → Dán vào đây
                <br />
                <span className="text-orange-300/50">Supports: JPG, PNG, GIF, WebP, etc.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Audio Section */}
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-4 sm:p-4 mb-6 border border-purple-400/50 shadow-neon-purple">
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h3 className="text-base sm:text-xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 animate-gradient-x">
                ÂM THANH TỪ VỰNG
              </h3>
            </div>
            <span className="ml-auto text-xs bg-purple-400/20 border border-purple-400/50 text-purple-300 px-3 py-2 rounded-xl font-mono shadow-neon-purple">
              TÙY CHỌN
            </span>
          </div>

          <div className="relative mb-4">
            <input
              type="url"
              value={audioVocab}
              onChange={(e) => setAudioVocab(e.target.value)}
              className="w-full px-4 py-3 pl-12 bg-black/60 border border-purple-400/50 rounded-xl text-purple-100 placeholder-purple-500/70 focus:border-purple-400 focus:shadow-neon-purple transition-all duration-300 font-mono backdrop-blur-sm"
              placeholder="Nhập URL âm thanh hoặc để trống"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => playVocabAudio(audioVocab, wordJp)}
              className="group flex items-center justify-center gap-2 px-6 py-3 bg-black/60 border border-cyan-400/50 text-cyan-300 hover:text-cyan-100 rounded-xl font-mono font-medium shadow-neon-cyan hover:shadow-neon-cyan-intense transition-all duration-300 transform hover:scale-105 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!audioVocab && !wordJp.trim()}
            >
              {audioVocab ? "Thử âm thanh" : "Thử TTS"}
              <div className="absolute inset-0 border border-cyan-400/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
            </button>
            <button
              onClick={stopAllAudio}
              className="group flex items-center justify-center gap-2 px-6 py-3 bg-black/60 border border-red-400/50 text-red-300 hover:text-red-100 rounded-xl font-mono font-medium shadow-neon-pink hover:shadow-neon-pink-intense transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
              title="[DỪNG TẤT CẢ ÂM THANH]"
            >
              Dừng
            </button>
          </div>
        </div>

        {/* Example Section */}
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-2 sm:p-3 mb-6 border border-pink-400/50 shadow-neon-pink relative overflow-hidden">
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h3 className="text-base sm:text-xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-500 to-cyan-400 animate-gradient-x">
                CÂU VÍ DỤ & ÂM THANH
              </h3>
            </div>
            <span className="ml-auto text-xs bg-pink-400/20 border border-pink-400/50 text-pink-300 px-3 py-2 rounded-xl font-mono shadow-neon-pink">
              TÙY CHỌN
            </span>
          </div>

          <input
            type="text"
            value={exampleSentence}
            onChange={(e) => setExampleSentence(e.target.value)}
            className="w-full px-4 py-3 bg-black/60 border border-pink-400/50 rounded-xl text-pink-100 placeholder-pink-500/70 focus:border-pink-400 focus:shadow-neon-pink transition-all duration-300 font-mono backdrop-blur-sm mb-4"
            placeholder="Nhập câu ví dụ ngữ cảnh"
          />

          <input
            type="url"
            value={audioSentence}
            onChange={(e) => handleAudioSentenceChange(e.target.value)}
            className="w-full px-4 py-3 bg-black/60 border border-pink-400/50 rounded-xl text-pink-100 placeholder-pink-500/70 focus:border-pink-400 focus:shadow-neon-pink transition-all duration-300 font-mono backdrop-blur-sm mb-4"
            placeholder="URL âm thanh tùy chọn"
          />

          {audioSentence && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-black/60 border border-purple-400/50 rounded-xl shadow-neon-purple backdrop-blur-sm">
              <div>
                <label className="block text-xs sm:text-sm font-mono font-bold text-purple-300 mb-2">
                  THỜI GIAN BẮT ĐẦU
                </label>
                <input
                  type="number"
                  value={sentenceStartTime}
                  onChange={(e) => setSentenceStartTime(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-black/60 border border-purple-400/50 rounded-lg text-purple-100 placeholder-purple-500/70 focus:border-purple-400 focus:shadow-neon-purple transition-all duration-300 font-mono"
                  min="0"
                  step="0.1"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-mono font-bold text-purple-300 mb-2">
                  THỜI GIAN KẾT THÚC
                </label>
                <input
                  type="number"
                  value={sentenceEndTime}
                  onChange={(e) => setSentenceEndTime(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-black/60 border border-purple-400/50 rounded-lg text-purple-100 placeholder-purple-500/70 focus:border-purple-400 focus:shadow-neon-purple transition-all duration-300 font-mono"
                  min="0"
                  step="0.1"
                  placeholder="0.0"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => playSentenceAudio(audioSentence, sentenceStartTime, sentenceEndTime)}
                  className="group w-full px-4 py-2 bg-black/60 border border-purple-400/50 text-purple-300 hover:text-purple-100 rounded-lg font-mono font-medium shadow-neon-purple hover:shadow-neon-purple-intense transition-all duration-300 transform hover:scale-105 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed relative"
                  disabled={!audioSentence}
                >
                  <span className="flex items-center justify-center gap-2 relative z-10">
                    THỬ NGHIỆM
                  </span>
                  <div className="absolute inset-0 border border-purple-400/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleSubmit}
            className="group relative flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-black/60 border border-blue-400/50 text-blue-300 hover:text-blue-100 rounded-xl font-mono font-medium shadow-neon-cyan hover:shadow-neon-cyan-intense transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
          >
            {isEditMode ? "CẬP NHẬT TỪ VỰNG" : "TẠO VÀO CƠ SỞ DỮ LIỆU"}
            <div className="absolute inset-0 border border-blue-400/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
          </button>
          {isEditMode && (
            <button
              onClick={handleClose}
              className="group relative flex items-center justify-center gap-2 px-6 py-3 bg-black/60 border border-red-400/50 text-red-300 hover:text-red-100 rounded-xl font-mono font-medium shadow-neon-pink hover:shadow-neon-pink-intense transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              HỦY BỎ
              <div className="absolute inset-0 border border-red-400/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
            </button>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="group absolute top-3 right-3 p-2 bg-black/80 border border-red-400/50 text-red-400 hover:text-red-100 hover:border-red-400 rounded-xl transition-all duration-300 hover:shadow-neon-pink backdrop-blur-sm font-mono"
          title="ĐÓNG HỆ THỐNG NHẬP"
        >
          <svg className="w-5 h-5 transform group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <div className="absolute inset-0 border border-red-400/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
        </button>
      </div>
    </div>
  );
});

export default AddVocabularyForm; 